import { observable, when } from 'mobx';

import { Config } from '@ratehub/base-ui';

import PAGES from '../definitions/Pages';


function createCoverageDetailsPageVM(context) {
    if (!context) {
        throw new RangeError('Context provided to view model is empty', 'createCoverageDetailsPageVM.js');
    }

    const state = observable({
        context,

        name: PAGES.COVERAGE_DETAILS.id,
        hasBeenVisited: false,
        isCompleted: false,

        get isActive() {
            return context.viewStore?.activePageName === state.name;
        },

        get values() {
            return {
                get name() {
                    return state.name;
                },

                // Common values
                get city() {
                    return context.journey?.applicant?.homeAddress?.city
                    // This shape is required because we need to match the City model from base-ui
                    && {
                        name: context.journey?.applicant?.homeAddress?.city,
                        province: {
                            code: context.journey?.applicant?.homeAddress?.province,
                        },
                    };
                },
                get hasConsumedTobacco() { // Only for standard and disability
                    return context.journey?.applicant?.hasConsumedTobacco;
                },
                get wantsBundledQuote() {
                    return context.journey?.wantsBundledQuote;
                },

                // Standard values
                get coverageAmount() {
                    return context.journey?.coverageAmount;
                },
                get isJointPolicy() {
                    return context.journey?.isJointPolicy;
                },
                
                // Disability values
                get isCanadianCitizen() {
                    return context.journey?.applicant?.isCanadianCitizen;
                },
                get grossAnnualIncome() {
                    return context.journey?.applicant?.grossAnnualIncome;
                },
                get occupation() {
                    return context.journey?.applicant?.occupation;
                },
                get disabilityCoverageAmount() {
                    return context.journey?.disabilityCoverageAmount;
                },

                // Group values
                get companyName() {
                    return context.journey?.companyName;
                },
                get businessAddress() {
                    return context.journey?.businessAddress;
                },
                get numberOfEmployees() {
                    return context.journey?.numberOfEmployees;
                },
            };
        },

        setHasBeenVisited() {
            state.hasBeenVisited = true;
        },

        submit(values) {
            context.updateJourney(transformValuesToModel(values));

            // We want to allow the user to push a new lead any time there is a change to their information
            if (context.sessionStore.hasDocumentUnsavedChanges(context.activeDocument.id)) {
                context.setShouldPushNewLead(true);
            }

            state.isCompleted = true;
            context.viewStore.showPersonalDetailsPage();
        },
    });

    // When we're active, we want to state that this page has been visited for the breadcrumbs
    when(
        () => state.isActive,
        () => state.setHasBeenVisited,
        { name: 'CoverageDetailsPageVM.hasBeenVisitedWhen' },
    );

    return state;
}

function transformValuesToModel(values) {
    return {
        applicant: {
            homeAddress: {
                city: values.city?.name,
                province: values.city?.province?.code,
            },
            hasConsumedTobacco: values.hasConsumedTobacco,
            // Disability values
            isCanadianCitizen: values.isCanadianCitizen,
            grossAnnualIncome: values.grossAnnualIncome,
            occupation: values.occupation,
        },
        wantsBundledQuote: Config.ENABLE_INS_LIFE_BUNDLE_QUOTE_QUESTION // Cleanup in INS-3191
            ? values.wantsBundledQuote
            : false,
        // Standard values
        coverageAmount: values.coverageAmount,
        isJointPolicy: values.isJointPolicy,
        // Disability values
        disabilityCoverageAmount: values.disabilityCoverageAmount,
        // Group values
        companyName: values.companyName,
        businessAddress: values.businessAddress,
        numberOfEmployees: values.numberOfEmployees,
    };
}

export default createCoverageDetailsPageVM;
